import { SelfServiceUserAccount } from '@ekt-group/general-purpose-api-interfaces';
import { ArrowLeft, Grid01, Menu01, SearchLg, User01, X } from '@untitled-ui/icons-react';
import React, { ChangeEventHandler, ComponentPropsWithoutRef, forwardRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MenuItem } from '@/components/header/Header';
import Input from '@/components/ui/Input';
import { LogoutButton, useUser } from '@/features/auth';
import { UserAccountList } from '@/features/auth/components';
import { useMenuItems } from '@/hooks/use-menu-items';
import { useUserInfoStore } from '@/store';
import { cn } from '@/utils/cn';

import Button from '../ui/Button';

interface MobileHeaderProps extends ComponentPropsWithoutRef<'div'> {
  isMobileMenuVisible?: boolean;
  onHambugerButtonClick?: () => void;
  onItemSelect?: (item: MenuItem) => void;
  onAccountSelect?: (account: SelfServiceUserAccount) => void;
}

const MobileHeader = forwardRef<HTMLDivElement, MobileHeaderProps>(
  ({ className, isMobileMenuVisible, onHambugerButtonClick, onItemSelect, onAccountSelect }, ref) => {
    const { t, i18n } = useTranslation();
    const { data: user } = useUser();
    const { name: accountName } = useUserInfoStore((state) => state.selectedAccount);

    const [searchValue, setSearchValue] = useState('');
    const [shouldShowAccountList, setShouldShowAccountList] = useState(false);
    const { menuItems } = useMenuItems();

    const handleUserClick = () => {
      setShouldShowAccountList(true);
    };

    const handleSearchValueChange: ChangeEventHandler<HTMLInputElement> = (event) => {
      setSearchValue(event.currentTarget.value);
    };

    const handleClearButtonClick = () => {
      setSearchValue('');
    };

    const handleUsersBackButtonClick = () => {
      setShouldShowAccountList(false);
    };

    return (
      <div className={cn(className, '')} ref={ref}>
        <button onClick={onHambugerButtonClick} className="relative rounded-lg text-brand-brown size-10">
          <Menu01
            className={cn('top-2 left-2 absolute text-3xl animate-fade animate-duration-500', {
              hidden: isMobileMenuVisible,
            })}
          />
          <X
            className={cn('top-2 left-2 absolute text-3xl animate-fade animate-duration-500', {
              hidden: !isMobileMenuVisible,
            })}
            onClick={handleClearButtonClick}
          />
        </button>

        {isMobileMenuVisible && (
          <>
            <div className={cn('top-[69px] md:top-[116px] bottom-0 z-10 fixed inset-x-0 flex flex-col bg-gray-100')}>
              <div className="flex flex-col overflow-y-auto gap-4 flex-1 py-4 animate-fade-down animate-duration-500">
                <div className="flex flex-col gap-y-5 bg-white p-5 rounded-[14px] w-full">
                  <div className="flex flex-col gap-y-2">
                    <Button
                      className={cn('flex', {
                        hidden: !shouldShowAccountList,
                      })}
                      variant="primaryAlt"
                      onClick={handleUsersBackButtonClick}
                    >
                      <ArrowLeft />
                      <span>{t('back', { ns: 'common' })}</span>
                    </Button>
                    <Button
                      className={cn('flex rounded-[8px]', {
                        hidden: shouldShowAccountList,
                      })}
                      variant="primaryAlt"
                      href={`${import.meta.env.VITE_EKKT_HOMEPAGE_URL}?lang=${i18n.language}`}
                    >
                      <Grid01 />
                      <span>{t('mainPage', { ns: 'header' })}</span>
                    </Button>
                    {user && (
                      <Button variant="primary" className="rounded-[8px]" onClick={handleUserClick}>
                        <User01 />
                        <span>{accountName}</span>
                      </Button>
                    )}
                  </div>

                  {/* general search */}
                  {!shouldShowAccountList && (
                    <Input
                      placeholder={t('search', { ns: 'header' })}
                      value={searchValue}
                      onChange={handleSearchValueChange}
                      leftSide={<SearchLg className="text-brand-brown" />}
                      rightSide={
                        <X
                          className={cn('text-brand-brown hover:text-stone-700 cursor-pointer', {
                            hidden: !searchValue,
                          })}
                          onClick={handleClearButtonClick}
                        />
                      }
                    />
                  )}

                  {shouldShowAccountList ? (
                    <UserAccountList
                      accounts={user?.accounts}
                      onAccountSelect={(account) => {
                        onAccountSelect(account);
                        setShouldShowAccountList(false);
                      }}
                    />
                  ) : (
                    user && (
                      <div className="gap-y-2 divide-y divide-y-brand-light animate-fade-down">
                        {menuItems
                          .filter((menuItem) => menuItem.isVisible)
                          .map((item, index) => (
                            <Button
                              onClick={() => {
                                onItemSelect?.(item);
                              }}
                              key={item.key}
                              variant="secondary"
                              className="justify-start w-full gap-x-3 hover:bg-slate-100 drop-shadow-none px-5 py-8 rounded-none text-left"
                            >
                              <span className="text-transparent stroke-brand-brown">{item.icon}</span>
                              <span className="text-sm text-black">
                                {t(item.key, {
                                  ns: 'header',
                                })}
                              </span>
                            </Button>
                          ))}
                      </div>
                    )
                  )}
                </div>
              </div>
              <div className="flex justify-center py-5 px-6">
                <LogoutButton className="z-10 w-full rounded-[8px]" />
              </div>
            </div>
          </>
        )}
      </div>
    );
  },
);

MobileHeader.displayName = 'MobileHeader';

export default MobileHeader;
